import { useTranslation } from "react-i18next";
import { type DocumentSummary } from "~/api/types";
import { Checkbox } from "~/atoms/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { type Option } from "~/molecules/ControlledCombobox";
import { StatusBadge } from "~/molecules/StatusBadge";
import { ComboboxCell } from "~/molecules/onboard-documents/ComboboxCell";
import { DocumentStatus } from "~/config/types";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { useBulkSelection } from "~/hooks/useBulkSelection";

interface Props {
  documents: DocumentSummary[];
  categoryOptions: Option[];
  targetCategoryByDocumentId: Map<number, string>;
  disabled: boolean;
  onTargetCategoryChange: (
    documentIds: Set<number>,
    targetCategoryId: string,
  ) => void;
}

export const DocumentReassignmentTable = ({
  documents,
  categoryOptions,
  targetCategoryByDocumentId,
  disabled,
  onTargetCategoryChange,
}: Props) => {
  const { t } = useTranslation();
  const {
    selectedIds,
    allSelected,
    toggleRow,
    toggleAll,
    clearSelection,
    bulkTargets,
  } = useBulkSelection(documents.map((document) => document.id));
  const { onHover, onHoverLeave, isHighlighted } =
    useBulkHoverHighlight<number>(selectedIds);

  return (
    <div className="flex flex-col gap-1">
      <BulkSelectionBar
        count={selectedIds.size}
        onUnselectAll={clearSelection}
        disabled={disabled}
      />
      <Table
        className="table-fixed"
        wrapperClassName="max-h-[60vh] rounded-md border"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                disabled={disabled}
              />
            </TableHead>
            <TableHead>
              {t(($) => $.modal.categoryTierModal.documentTitle)}
            </TableHead>
            <TableHead>
              {t(($) => $.modal.categoryTierModal.moveToCategory)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow key={document.id}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(document.id)}
                  onCheckedChange={() => toggleRow(document.id)}
                  disabled={disabled}
                />
              </TableCell>
              <TableCell>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate">{document.name}</span>
                  {document.archivedAt ? (
                    <StatusBadge.DocumentStatus
                      status={DocumentStatus.archived}
                    />
                  ) : document.latestPublishedVersion ? (
                    <StatusBadge.DocumentVersionStatus
                      status={document.latestPublishedVersion.status}
                    />
                  ) : (
                    <StatusBadge.DocumentVersionStatus
                      status={document.latestVersion.status}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <ComboboxCell
                  value={targetCategoryByDocumentId.get(document.id) ?? null}
                  onChange={(value) =>
                    onTargetCategoryChange(bulkTargets(document.id), value)
                  }
                  options={categoryOptions}
                  placeholder={t(($) => $.commons.customSelect.selectCategory)}
                  disabled={disabled}
                  highlighted={isHighlighted(document.id)}
                  onHover={() => onHover(document.id)}
                  onHoverLeave={onHoverLeave}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
